import React, { useState } from 'react';
import axios from 'axios';
import {
  FaInfoCircle, FaTrash, FaBell, FaFileAlt, FaDownload,
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, renderReminderTracking, formInputClass } from './menuTabUtils';

const RegisteredAgentTab = ({
  selectedMenuUser,
  handleRegisteredAgentDueDateUpdate,
  handleRegisteredAgentDocumentUpload,
  handleRegisteredAgentDocumentDelete,
}) => {
  const [registeredAgentDueDate, setRegisteredAgentDueDate] = useState('');
  const [registeredAgentUploading, setRegisteredAgentUploading] = useState(false);
  const [manualReminderLoading, setManualReminderLoading] = useState(false);

  const handleSendManualReminder = async (userId) => {
    if (!window.confirm('Send a registered agent reminder email to this client now?')) return;
    setManualReminderLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-immediate-registered-agent-reminder`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Reminder email sent successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder email.');
    } finally {
      setManualReminderLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h5 className="font-bold text-gray-900 dark:text-white">Registered Agent Service - Due Date Setup</h5>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Renewal Due Date</label>
          <FaInfoCircle className="w-4 h-4 text-primary-500" title="Automatic reminders: 1 month and 15 days before due date" />
        </div>
        <div className="flex gap-2">
          <input
            type="date"
            value={registeredAgentDueDate || formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate)}
            onChange={(e) => setRegisteredAgentDueDate(e.target.value)}
            className={`${formInputClass} flex-1`}
          />
          <button
            type="button"
            onClick={() => {
              const d = registeredAgentDueDate || formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate);
              if (d) {
                handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, d);
                setRegisteredAgentDueDate('');
              }
            }}
            disabled={!registeredAgentDueDate && !selectedMenuUser?.registeredAgentService?.dueDate}
            className="px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
          >
            Update
          </button>
          {selectedMenuUser?.registeredAgentService?.dueDate && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Are you sure you want to remove the Registered Agent due date?')) {
                  handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, null);
                }
              }}
              className="px-4 py-2.5 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-600 hover:text-white border border-red-200"
              title="Remove Due Date"
            >
              <FaTrash className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {selectedMenuUser?.registeredAgentService?.dueDate && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
          <h6 className="font-bold text-blue-800 dark:text-blue-300 mb-2">Current Registered Agent Due Date</h6>
          <div className="mt-2 p-3 bg-white dark:bg-neutral-900 rounded-lg">
            <strong className="text-primary-600">Renewal Due Date:</strong>
            <span className="text-red-600 font-bold ml-2">{formatDateForDisplay(selectedMenuUser.registeredAgentService.dueDate)}</span>
          </div>
          <div className="mt-3">
            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">Automated Reminder Tracking</p>
            {renderReminderTracking(
              selectedMenuUser.registeredAgentService.oneMonthReminderSent,
              selectedMenuUser.registeredAgentService.fifteenDayReminderSent
            )}
          </div>
          <button
            type="button"
            onClick={() => handleSendManualReminder(selectedMenuUser._id)}
            disabled={manualReminderLoading}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-xs font-bold rounded-xl hover:bg-primary-700 disabled:opacity-50"
          >
            {manualReminderLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <FaBell className="w-3.5 h-3.5" />
                Send Manual Reminder Now
              </>
            )}
          </button>
        </div>
      )}

      <div className="pt-4 border-t border-gray-100 dark:border-neutral-700 mt-4">
        <h6 className="font-bold text-gray-900 dark:text-white mb-3">Upload Registered Agent Documents</h6>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Upload documents visible to the user in their Registered Agent section.</p>
        <input
          type="file"
          multiple
          onChange={async (e) => {
            if (e.target.files && e.target.files.length > 0) {
              setRegisteredAgentUploading(true);
              try {
                await handleRegisteredAgentDocumentUpload(selectedMenuUser._id, e.target.files);
              } finally {
                setRegisteredAgentUploading(false);
              }
              e.target.value = '';
            }
          }}
          disabled={registeredAgentUploading}
          className="w-full px-4 py-3 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-600 file:text-white file:font-semibold disabled:opacity-50"
        />
      </div>

      {selectedMenuUser?.registeredAgentDocuments?.length > 0 && (
        <div className="mt-6 border-t border-gray-100 dark:border-neutral-700 pt-4">
          <h6 className="font-bold text-gray-900 dark:text-white mb-3">Uploaded Documents</h6>
          <div className="space-y-2">
            {selectedMenuUser.registeredAgentDocuments.map((doc, idx) => (
              <div key={doc._id || idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700">
                <div className="flex items-center gap-3 min-w-0">
                  <FaFileAlt className="w-5 h-5 text-primary-500 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white block truncate">{doc.originalName}</span>
                    <span className="text-[10px] text-gray-500">{new Date(doc.date).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="Download"
                  >
                    <FaDownload className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRegisteredAgentDocumentDelete(selectedMenuUser._id, doc._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredAgentTab;
